/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FormArray } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { BaseInput, BaseInputConstructor } from './base-input';
import { InputContainer, InputContainerConstructor } from './input-container';
/**
 * Represents the configuration options for creating a form array.
 *
 */
export interface ArrayConstructor<T> extends BaseInputConstructor<T[]> {
    /**
     * Configuration options for the form controls.
     * An object where the keys represent the control names and the values represent the corresponding input components.
     *
     */
    rowConfig: InputContainerConstructor;

    /**
     * Max number of row that we want to show
     */
    maxLength?: number;

    /**
     * Indicates whether to skip the destruction of the form.
     *
     */
    skipDestroy?: boolean;

    btnAddStyleClass?:
        | 'success'
        | 'info'
        | 'warn'
        | 'danger'
        | 'help'
        | 'primary'
        | 'secondary'
        | 'contrast'
        | null
        | undefined;

    btnRemoveStyleClass?:
        | 'success'
        | 'info'
        | 'warn'
        | 'danger'
        | 'help'
        | 'primary'
        | 'secondary'
        | 'contrast'
        | null
        | undefined;

    /**
     * Callback function to be executed when the form is destroyed.
     *
     */
    onDestroy?: () => void;
}

export class ArrayInput<T = any> extends FormArray<InputContainer<T>> {
    /**
     * The type of the input control, set to `'array'`.
     */
    inputType = 'array';

    /**
     * An array of CSS class names for the form group element.
     */
    formGroupClass?: string[];

    /**
     * Input label.
     */
    readonly label?: string;

    /**
     * Input placeholder text.
     */
    readonly placeholder?: string;

    /**
     * Index of the input in the related input array.
     */
    readonly index?: number;

    /**
     * Additional CSS classes for the input label.
     */
    readonly labelClass?: string[];

    /**
     * Additional CSS classes for the input.
     */
    readonly inputClass?: string[];

    /**
     * Max number of row that we want to show
     */
    readonly maxLength?: number;

    /**
     * Event emitted when the input status changes.
     */
    // @ts-ignore
    readonly inputStatusChanges: Observable<any>;

    readonly btnAddStyleClass:
        | 'success'
        | 'info'
        | 'warn'
        | 'danger'
        | 'help'
        | 'primary'
        | 'secondary'
        | 'contrast'
        | null
        | undefined = 'primary';
    readonly btnRemoveStyleClass:
        | 'success'
        | 'info'
        | 'warn'
        | 'danger'
        | 'help'
        | 'primary'
        | 'secondary'
        | 'contrast'
        | null
        | undefined = 'secondary';

    /**
     * Subscriptions to input events.
     */
    private _subscriptions: Subscription[] = [];
    /**
     * Callback function to be executed when the form is destroyed.
     *
     */
    private _onDestroy?: () => void;
    private _statusArray: { inserted: boolean; modified: boolean; deleted: boolean }[] = [];
    private _rowConfig: InputContainerConstructor;
    private _rowIds: string[] = [];

    constructor(options: ArrayConstructor<T>) {
        super([new InputContainer(options.rowConfig)], options.validatorOrOpts, options.asyncValidator);
        this.formGroupClass = options.formGroupClass || [];

        const keys = Object.keys(options.rowConfig.controls);

        const controls: {
            [key: string]: BaseInput<T>;
        } = {};

        keys.forEach((k) => {
            controls[k] = options.rowConfig.controls[k].clone();
        });

        this._rowConfig = { ...options.rowConfig, controls };

        this.controls.forEach((row: any, index: any) => {
            const rowId = this._getRowId();
            this._rowIds.push(rowId);
            keys.forEach((key) => {
                this._initValueRowInputChanges(row.controls[key], rowId);
            });
        });

        this.label = options.label;
        this.formGroupClass = options.formGroupClass || [];
        this.labelClass = options.labelClass || [];
        this.inputClass = options.inputClass || [];

        if ('undefined' !== typeof options.btnAddStyleClass) {
            this.btnAddStyleClass = options.btnAddStyleClass;
        }

        if ('undefined' !== typeof options.btnRemoveStyleClass) {
            this.btnRemoveStyleClass = options.btnRemoveStyleClass;
        }

        if (options.valueChange) {
            this.valueChange = options.valueChange;
            this._initValueChanges();
        }

        if (options.maxLength) {
            this.maxLength = options.maxLength;
        }

        this.controls.forEach(() => {
            this._statusArray.push({ inserted: false, modified: false, deleted: false });
        });

        this._subscriptions.push(
            this.valueChanges.subscribe(() => {
                this.controls.forEach((control: any, index: any) => {
                    if (
                        this._statusArray[index] &&
                        !this._statusArray[index]?.deleted &&
                        !this._statusArray[index]?.inserted
                    ) {
                        this._statusArray[index].modified = true;
                    }
                });
            })
        );

        this._onDestroy = options.onDestroy;
    }

    /**
     * Callback for value changes of the input.
     */
    readonly valueChange: (value: T[]) => void = (value) => {};

    /**
     * Pushes a new row to the form array and updates the status array.
     * If passed value it will set the new row with value received
     *
     * @param value - The value to be pushed.
     */
    override push(value?: Record<string, any>): void {
        const controls: {
            [key: string]: BaseInput<T>;
        } = {};
        const keys = Object.keys(this._rowConfig.controls);

        const rowId = this._getRowId();
        this._rowIds.push(rowId);

        keys.forEach((k) => {
            controls[k] = this._rowConfig.controls[k].clone();
            this._initValueRowInputChanges(controls[k], rowId);
        });

        const container = new InputContainer({ ...this._rowConfig, controls });

        super.push(container);
        this._statusArray.push({ inserted: true, modified: false, deleted: false });
        if (value) {
            container.setValue(value);
        }
    }

    override removeAt(index: number): void {
        super.removeAt(index);
        this._rowIds.splice(index, 1);
        this._statusArray[index].deleted = true;
    }

    patchAll(values: Record<string, any>[]) {
        this.patchValue(values);
    }

    override patchValue(values: Record<string, any>[]) {
        if (!values || values.length === 0) {
            return;
        }
        this.clear({ emitEvent: false });
        values.forEach(() => this.push());

        super.patchValue(values);
    }

    override clear(options?: { emitEvent?: boolean }) {
        this._rowIds = [];
        this._statusArray = [];
        super.clear(options);
    }

    getStatus(index: number): { inserted: boolean; modified: boolean; deleted: boolean } {
        return this._statusArray[index];
    }

    setIndex(index: number): void {
        (this as { index: number }).index = index;
    }

    /**
     * Performs cleanup tasks when the form is destroyed.
     *
     */
    onDestroy(): void {
        if (this._onDestroy && typeof this._onDestroy === 'function') {
            this._onDestroy();
        }

        this.unsubscribeSubscriptions();
    }

    unsubscribeSubscriptions(): void {
        this._subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
    }

    /** @internal */
    private _getRowId() {
        return `row_${Date.now()}_${this._pseudoRandom()} `;
    }

    /** @internal */
    private _pseudoRandom() {
        const array = new Uint32Array(10);
        return window.crypto.getRandomValues(array)[Math.floor(Math.random() * 9)];
    }

    /** @internal */
    private _initValueRowInputChanges(control: BaseInput<any>, rowId: string): void {
        // inizializza evento value changes
        control.ignoreValueChanges = true;
        this._subscriptions.push(
            control.valueChanges.subscribe((value) => this._nextValueChanges(control, rowId, value))
        );
    }

    /** @internal */
    private _nextValueChanges(control: BaseInput<any>, rowId: string, value: any) {
        if (!control.valueChange) {
            return;
        }

        const index = this._rowIds.indexOf(rowId);
        control.valueChange(value, index);
    }

    /** @internal */
    private _initValueChanges(): void {
        // inizializza evento value changes
        this._subscriptions.push(this.valueChanges.subscribe(this.valueChange));
    }
}
